// @ts-nocheck
import { useState, useEffect, useCallback } from 'react'
import { zodResolver } from "@hookform/resolvers/zod"
import { useForm } from "react-hook-form"
import * as z from "zod"
import { Button } from "@/components/ui/button"
import _ from 'lodash';
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Switch } from "@/components/ui/switch"
import { CornerLeftUp } from 'lucide-react'
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion"
import helpers from '@/utils/helpers';
import { Checkbox } from "@/components/ui/checkbox"
import { STAT_METADATA_BY_SPORT, basketballStatMetadata, STAT_FIELDS_BY_SPORT } from '@staty/shared/schemas/stat';
import { CONFIG_DEFAULTS, defaultGameStats } from '@staty/shared/utils/configDefaults';

const parseNumberInput = (value: string): number | undefined => {
  const parsed = parseInt(value, 10);
  return isNaN(parsed) ? undefined : Math.max(0, parsed);
};

const formSchema = z.object({
  // TOP-LEVEL entity field (tournament/league only) — NOT part of config and
  // deliberately kept out of the config payload (see onSubmit).
  startDate: z.string().optional(),
  requirePlayers: z.boolean(),
  minPlayers: z.string().optional(),
  maxPlayers: z.string().optional(),
  requireCoaches: z.boolean(),
  minCoaches: z.string().optional(),
  maxCoaches: z.string().optional(),
  disableSync: z.boolean(),
  hideTeams: z.boolean(),
  hideSchedules: z.boolean(),
  hideStandings: z.boolean(),
  showAllTeams: z.boolean(),
  // Stats visibility + which stat columns are enabled
  showStats: z.boolean().optional(),
  gameStats: z.array(z.string()).optional(),
  // How public aggregate stats display: per-game average vs cumulative total
  statDisplayMode: z.enum(['average', 'total']).optional(),
  // Division capacity fields
  enableCapacity: z.boolean(),
  capacity: z.string().optional(),
  hasWaitlist: z.boolean(),
  autoPromote: z.boolean().optional(),
  promotionPaymentWindowHours: z.string().optional(),
  // Division eligibility (constraints) fields — only surfaced when entityType === 'division'
  genderMale: z.boolean().optional(),
  genderFemale: z.boolean().optional(),
  genderOther: z.boolean().optional(),
  minRating: z.string().optional(),
  maxRating: z.string().optional(),
  minAge: z.string().optional(),
  maxAge: z.string().optional(),
}).refine((data) => {
  if (data.requirePlayers) {
    const minPlayers = parseNumberInput(data.minPlayers || '');
    const maxPlayers = parseNumberInput(data.maxPlayers || '');
    if (minPlayers === undefined || maxPlayers === undefined) return true;
    return minPlayers <= maxPlayers;
  }
  return true;
}, {
  message: "Minimum players cannot be greater than maximum players",
  path: ["minPlayers"],
}).refine((data) => {
  if (data.requireCoaches) {
    const minCoaches = parseNumberInput(data.minCoaches || '');
    const maxCoaches = parseNumberInput(data.maxCoaches || '');
    if (minCoaches === undefined || maxCoaches === undefined) return true;
    return minCoaches <= maxCoaches;
  }
  return true;
}, {
  message: "Minimum coaches cannot be greater than maximum coaches",
  path: ["minCoaches"],
}).refine((data) => {
  const min = parseFloat(data.minRating || '');
  const max = parseFloat(data.maxRating || '');
  if (!isNaN(min) && !isNaN(max)) return min <= max;
  return true;
}, {
  message: "Min rating cannot exceed max rating",
  path: ["minRating"],
}).refine((data) => {
  const min = parseInt(data.minAge || '', 10);
  const max = parseInt(data.maxAge || '', 10);
  if (!isNaN(min) && !isNaN(max)) return min <= max;
  return true;
}, {
  message: "Min age cannot exceed max age",
  path: ["minAge"],
});

type FormValues = z.infer<typeof formSchema>

type SettingsType = {
  minPlayers?: number;
  maxPlayers?: number;
  minCoaches?: number;
  maxCoaches?: number;
  requirePlayers?: boolean;
  requireCoaches?: boolean;
  disableSync?: boolean;
  hideTeams?: boolean;
  hideSchedules?: boolean;
  showAllTeams?: boolean;
  showStats?: boolean;
  gameStats?: Record<string, string[]>;
}

/** Top-level entity fields (NOT config) the drawer can edit. */
export interface TopLevelSettings {
  startDate?: string;
}

/** Named parent tier for inheritance provenance, most-specific first
 *  (division drawer: [contest/league, tournament]; league drawer: [tournament]). */
export interface ParentTier {
  tier: string; // 'contest' | 'league' | 'tournament'
  name: string;
  config: any;
}

interface TeamSettingsFormProps {
  onSubmitSuccess: (values: SettingsType, topLevel?: TopLevelSettings) => void
  sport: string,
  data: any,
  defaultConfig: SettingsType,
  parentTiers?: ParentTier[],
  entityType?: 'division' | 'league' | 'tournament',
}

// Platform-default seeds come from CONFIG_DEFAULTS (ADR 0035). Only the
// per-sport min/maxPlayers are FORM SEEDS (suggested roster sizes for the
// sport, not platform defaults — the platform default is 1/unlimited).
const SPORTS_CONFIG = {
  basketball: {
    defaults: {
      requirePlayers: CONFIG_DEFAULTS.requirePlayers,
      requireCoaches: CONFIG_DEFAULTS.requireCoaches,
      disableSync: CONFIG_DEFAULTS.disableSync,
      hideTeams: CONFIG_DEFAULTS.hideTeams,
      hideSchedules: CONFIG_DEFAULTS.hideSchedules,
      minPlayers: '4', // FORM SEED, not a platform default (ADR 0035)
      maxPlayers: '11', // FORM SEED, not a platform default (ADR 0035)
      minCoaches: String(CONFIG_DEFAULTS.minCoaches),
      maxCoaches: String(CONFIG_DEFAULTS.maxCoaches),
    },
    internal: {
      MAX_PLAYERS: 20,
      MAX_COACHES: 8,
    }
  },
  soccer: {
    defaults: {
      hideTeams: CONFIG_DEFAULTS.hideTeams,
      hideSchedules: CONFIG_DEFAULTS.hideSchedules,
      minPlayers: '5', // FORM SEED, not a platform default (ADR 0035)
      maxPlayers: '11', // FORM SEED, not a platform default (ADR 0035)
      minCoaches: String(CONFIG_DEFAULTS.minCoaches),
      maxCoaches: String(CONFIG_DEFAULTS.maxCoaches),
    },
    internal: {
      MAX_PLAYERS: 20,
      MAX_COACHES: 8,
    }
  },
  volleyball: {
    defaults: {
      requirePlayers: CONFIG_DEFAULTS.requirePlayers,
      requireCoaches: CONFIG_DEFAULTS.requireCoaches,
      disableSync: CONFIG_DEFAULTS.disableSync,
      hideTeams: CONFIG_DEFAULTS.hideTeams,
      hideSchedules: CONFIG_DEFAULTS.hideSchedules,
      minPlayers: '6', // FORM SEED, not a platform default (ADR 0035)
      maxPlayers: '12', // FORM SEED, not a platform default (ADR 0035)
      minCoaches: String(CONFIG_DEFAULTS.minCoaches),
      maxCoaches: String(CONFIG_DEFAULTS.maxCoaches),
    },
    internal: {
      MAX_PLAYERS: 20,
      MAX_COACHES: 8,
    }
  },
  pickleball: {
    defaults: {
      requirePlayers: CONFIG_DEFAULTS.requirePlayers,
      requireCoaches: CONFIG_DEFAULTS.requireCoaches,
      disableSync: CONFIG_DEFAULTS.disableSync,
      hideTeams: CONFIG_DEFAULTS.hideTeams,
      hideSchedules: CONFIG_DEFAULTS.hideSchedules,
      minPlayers: '2', // FORM SEED, not a platform default (ADR 0035)
      maxPlayers: '4', // FORM SEED, not a platform default (ADR 0035)
      minCoaches: String(CONFIG_DEFAULTS.minCoaches),
      maxCoaches: String(CONFIG_DEFAULTS.maxCoaches),
    },
    internal: {
      MAX_PLAYERS: 20,
      MAX_COACHES: 8,
    }
  },
  softball: {
    defaults: {
      requirePlayers: CONFIG_DEFAULTS.requirePlayers,
      requireCoaches: CONFIG_DEFAULTS.requireCoaches,
      disableSync: CONFIG_DEFAULTS.disableSync,
      hideTeams: CONFIG_DEFAULTS.hideTeams,
      hideSchedules: CONFIG_DEFAULTS.hideSchedules,
      minPlayers: '9', // FORM SEED, not a platform default (ADR 0035)
      maxPlayers: '16', // FORM SEED, not a platform default (ADR 0035)
      minCoaches: String(CONFIG_DEFAULTS.minCoaches),
      maxCoaches: String(CONFIG_DEFAULTS.maxCoaches),
    },
    internal: {
      MAX_PLAYERS: 25,
      MAX_COACHES: 8,
    }
  },
  baseball: {
    defaults: {
      requirePlayers: CONFIG_DEFAULTS.requirePlayers,
      requireCoaches: CONFIG_DEFAULTS.requireCoaches,
      disableSync: CONFIG_DEFAULTS.disableSync,
      hideTeams: CONFIG_DEFAULTS.hideTeams,
      hideSchedules: CONFIG_DEFAULTS.hideSchedules,
      minPlayers: '9', // FORM SEED, not a platform default (ADR 0035)
      maxPlayers: '16', // FORM SEED, not a platform default (ADR 0035)
      minCoaches: String(CONFIG_DEFAULTS.minCoaches),
      maxCoaches: String(CONFIG_DEFAULTS.maxCoaches),
    },
    internal: {
      MAX_PLAYERS: 25,
      MAX_COACHES: 8,
    }
  },
}

export function TeamSettingsForm({ onSubmitSuccess, sport, data, defaultConfig, parentTiers, entityType }: TeamSettingsFormProps) {
  // Eligibility lives under config.constraints and inherits via the standard
  // division > league > tournament (registration is NOT a tier — ADR 0034) hierarchy, so it surfaces
  // on all three of those entity types.
  const showConstraints = entityType === 'division' || entityType === 'league' || entityType === 'tournament';
  // startDate lives on the tournament/league document itself (divisions have no
  // dates) — the legacy Tournaments/Leagues tabs were the only editor for it
  // before their 2026-08-24 nav removal.
  const showStartDate = entityType === 'tournament' || entityType === 'league';
  const [isSubmitting, setIsSubmitting] = useState(false)
  const sportConfig = SPORTS_CONFIG[sport] ? SPORTS_CONFIG[sport] : SPORTS_CONFIG.basketball;
  const currentConfig = {
    ...sportConfig.defaults,
    ...defaultConfig,
    ..._.get(data, 'config', {})
  }
  // Capacity & hasWaitlist inherit together: when the entity has no own capacity
  // (>0), display the parent's effective values so users can see what's actually
  // in effect. Otherwise the form would always show "disabled" for fresh entities
  // because Mongoose defaults capacity=0/hasWaitlist=false.
  const ownCapacity = _.get(data, 'config.capacity', 0) || 0;
  const hasOwnCapacity = ownCapacity > 0;
  const parentCapacity = defaultConfig?.capacity || 0;
  const parentHasWaitlist = !!defaultConfig?.hasWaitlist;
  const parentAutoPromote = !!defaultConfig?.autoPromote;
  const parentPromotionWindow = defaultConfig?.promotionPaymentWindowHours;
  const effectiveCapacity = hasOwnCapacity ? ownCapacity : parentCapacity;
  const effectiveHasWaitlist = hasOwnCapacity ? !!_.get(data, 'config.hasWaitlist') : parentHasWaitlist;
  const effectiveAutoPromote = hasOwnCapacity ? !!_.get(data, 'config.autoPromote') : parentAutoPromote;
  const effectivePromotionWindow = hasOwnCapacity
    ? _.get(data, 'config.promotionPaymentWindowHours')
    : parentPromotionWindow;
  console.log('TeamSettingsForm::currentConfig vs defaults::', currentConfig, sportConfig.defaults, defaultConfig);
  // Own-first / parent-fallback per type so the modal mirrors the row chips.
  // Saving with these prefilled values will persist them as the entity's own
  // config (same lock-in behaviour as capacity above). Eligibility now sits
  // under `config.eligibility` on the entity and on `defaultConfig`.
  const constraintGender = (() => {
    const own = _.get(data, 'config.eligibility.gender');
    if (Array.isArray(own) && own.length > 0) return own;
    const parent = _.get(defaultConfig, 'eligibility.gender');
    return Array.isArray(parent) ? parent : [];
  })();
  const cfgRating = (() => {
    const own = _.get(data, 'config.eligibility.rating');
    if (own && (own.minRating != null || own.maxRating != null)) return own;
    return _.get(defaultConfig, 'eligibility.rating') || {};
  })();
  const cfgAge = (() => {
    const own = _.get(data, 'config.eligibility.age');
    if (own && (own.minAge != null || own.maxAge != null)) return own;
    return _.get(defaultConfig, 'eligibility.age') || {};
  })();
  // Stats visibility: own config.showStats → parent → show (the legacy org
  // hideStats fallback was retired — ADR 0035; script 25 stamped affected orgs).
  const showStatsDefault = (() => {
    const own = _.get(data, 'config.showStats');
    if (typeof own === 'boolean') return own;
    const parent = _.get(defaultConfig, 'showStats');
    if (typeof parent === 'boolean') return parent;
    return CONFIG_DEFAULTS.showStats;
  })();
  // Enabled stat columns for this sport: own → parent → sport default.
  const gameStatsDefault = (() => {
    const own = _.get(data, `config.gameStats.${sport}`);
    if (Array.isArray(own) && own.length) return own;
    const parent = _.get(defaultConfig, `gameStats.${sport}`);
    if (Array.isArray(parent) && parent.length) return parent;
    return defaultGameStats(sport) || [];
  })();
  // "Inherited" hint. A switch seeded from the parent looks identical to one the
  // admin deliberately set, which is exactly what made a tournament's Hide
  // Schedules look like it hadn't reached its contests and divisions. Show where
  // the value is coming from when this entity has none of its own.
  // Where a toggle's effective value comes from. Returns the winning PARENT
  // tier (most-specific first) when this entity doesn't set the field itself,
  // or an 'override' descriptor when it sets a value that differs from a
  // parent's. Named tiers come from `parentTiers`; when the caller didn't
  // provide them, fall back to the old generic wording via `defaultConfig`.
  const provenance = (field: string): null | {
    kind: 'inherited' | 'override';
    parentValue: boolean;
    tierLabel: string;
    name: string | null;
  } => {
    const own = _.get(data, `config.${field}`);
    const winner = (parentTiers || []).find((p) => typeof _.get(p?.config, field) === 'boolean');
    if (typeof own === 'boolean') {
      // Explicitly set here — only worth a note when it disagrees with a parent.
      if (winner && (_.get(winner.config, field) as boolean) !== own) {
        return { kind: 'override', parentValue: _.get(winner.config, field) as boolean, tierLabel: winner.tier, name: winner.name };
      }
      return null;
    }
    if (winner) {
      return { kind: 'inherited', parentValue: _.get(winner.config, field) as boolean, tierLabel: winner.tier, name: winner.name };
    }
    // Legacy fallback (no named tiers supplied): merged parent config only.
    const parent = _.get(defaultConfig, field);
    if (typeof parent === 'boolean') {
      return {
        kind: 'inherited',
        parentValue: parent,
        tierLabel: entityType === 'division' ? 'contest / tournament' : 'tournament',
        name: null,
      };
    }
    return null;
  };

  // The provenance pill under a visibility toggle. Three states:
  //  - Inherited (untouched): "Inherited · On — from <name> (<tier>)".
  //  - Inherited but the switch was just flipped (unsaved): "Overrides on
  //    save" so the admin knows the pill's tier value no longer applies.
  //  - Set here but a parent disagrees: "Overrides <name> (<tier> sets On)".
  const InheritedNote = ({ field }: { field: string }) => {
    const p = provenance(field);
    if (!p) return null;
    const current = form.watch(field as any);
    const onOff = (v: boolean) => (v ? 'On' : 'Off');
    const from = p.name
      ? (<><span className="font-medium text-foreground/80">{p.name}</span> <span className="text-muted-foreground/70">({p.tierLabel})</span></>)
      : (<>the {p.tierLabel}</>);

    if (p.kind === 'override') {
      return (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground" data-testid={`provenance-${field}`}>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-medium text-foreground/70">
            Set here
          </span>
          <span>overrides {from}, which sets {onOff(p.parentValue)}</span>
        </div>
      );
    }

    const pendingOverride = typeof current === 'boolean' && current !== p.parentValue;
    if (pendingOverride) {
      return (
        <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground" data-testid={`provenance-${field}`}>
          <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 font-medium text-foreground/70">
            Overrides on save
          </span>
          <span>{from} sets {onOff(p.parentValue)}. Saving keeps this {entityType}&apos;s own value</span>
        </div>
      );
    }

    return (
      <div className="mt-1.5 flex flex-wrap items-center gap-x-1.5 gap-y-1 text-xs text-muted-foreground" data-testid={`provenance-${field}`}>
        <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary">
          <CornerLeftUp className="h-3 w-3" aria-hidden="true" />
          Inherited · {onOff(p.parentValue)}
        </span>
        <span>from {from}. Flip the switch to override just this {entityType}</span>
      </div>
    );
  };

  const newDefaults = {
    ...sportConfig.defaults,
    ...helpers.convertNumbersToStrings(currentConfig || {}),
    // Top-level entity field, yyyy-MM-dd for <Input type="date"> (same display
    // pattern as RegistrationAdmin's Roster Lock Date).
    startDate: data?.startDate ? new Date(data.startDate).toISOString().split('T')[0] : '',
    showAllTeams: !!currentConfig.showAllTeams,
    hideStandings: !!currentConfig.hideStandings,
    showStats: showStatsDefault,
    gameStats: gameStatsDefault,
    statDisplayMode: _.get(currentConfig, 'statDisplayMode')
      || _.get(defaultConfig, 'statDisplayMode')
      || CONFIG_DEFAULTS.statDisplayMode,
    enableCapacity: effectiveCapacity > 0,
    capacity: effectiveCapacity > 0 ? String(effectiveCapacity) : undefined,
    hasWaitlist: effectiveHasWaitlist,
    autoPromote: effectiveAutoPromote,
    promotionPaymentWindowHours: effectivePromotionWindow !== undefined ? String(effectivePromotionWindow) : undefined,
    // Eligibility defaults (sourced from config.eligibility.gender / rating / age)
    genderMale: constraintGender.includes('male'),
    genderFemale: constraintGender.includes('female'),
    genderOther: constraintGender.includes('other'),
    minRating: cfgRating.minRating != null ? String(cfgRating.minRating) : '',
    maxRating: cfgRating.maxRating != null ? String(cfgRating.maxRating) : '',
    minAge: cfgAge.minAge != null ? String(cfgAge.minAge) : '',
    maxAge: cfgAge.maxAge != null ? String(cfgAge.maxAge) : '',
  }
  console.log('TeamSettingsForm::newDefaults::', newDefaults);
  const form = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: newDefaults
  })

  const requirePlayers = form.watch("requirePlayers")
  const requireCoaches = form.watch("requireCoaches")
  const hideTeams = form.watch("hideTeams")
  const enableCapacity = form.watch("enableCapacity")
  const hasWaitlist = form.watch("hasWaitlist")

  useEffect(() => {
    if (!requirePlayers) {
      form.setValue("minPlayers", undefined)
      form.setValue("maxPlayers", undefined)
    }
    if (!requireCoaches) {
      form.setValue("minCoaches", undefined)
      form.setValue("maxCoaches", undefined)
    }
    if (hideTeams) {
      form.setValue("hideTeams", true)
    }
  }, [requirePlayers, requireCoaches, hideTeams, form])

  useEffect(() => {
    if (!enableCapacity) {
      form.setValue("capacity", undefined)
      form.setValue("hasWaitlist", false)
      form.setValue("autoPromote", false)
      form.setValue("promotionPaymentWindowHours", undefined)
    }
  }, [enableCapacity, form])

  useEffect(() => {
    if (!hasWaitlist) {
      form.setValue("autoPromote", false)
      form.setValue("promotionPaymentWindowHours", undefined)
    }
  }, [hasWaitlist, form])

  const isFormValid = useCallback(() => {
    const values = form.getValues();

    if (values.requirePlayers) {
      const minPlayers = parseNumberInput(values.minPlayers || '');
      const maxPlayers = parseNumberInput(values.maxPlayers || '');
      if (minPlayers === undefined || maxPlayers === undefined || minPlayers > maxPlayers) return false;
    }
    if (values.requireCoaches) {
      const minCoaches = parseNumberInput(values.minCoaches || '');
      const maxCoaches = parseNumberInput(values.maxCoaches || '');
      if (minCoaches === undefined || maxCoaches === undefined || minCoaches > maxCoaches) return false;
    }
    return true;
  }, [form]);

  async function onSubmit(values: FormValues) {
    setIsSubmitting(true)
    try {
      console.log(values)
      await new Promise(resolve => setTimeout(resolve, 500)) // Simulating API call
      // Write a boolean ONLY when it differs from what this entity would inherit.
      // These switches are seeded from the parent when the entity has no value of
      // its own, so persisting them unconditionally stamped a copy of the
      // inherited value onto every entity whose Settings were ever saved — for
      // any unrelated reason — and `getConfig` is most-specific-wins, so that
      // froze the level and a later parent change could never reach it again.
      // `undefined` is dropped by JSON.stringify and the server $sets the whole
      // `config` subdoc, so an omitted key is REMOVED, which is exactly "inherit".
      // (Same contract as showStats/statDisplayMode/gameStats below, generalized.)
      const inheritedBool = (field: string, fallback = false) => {
        const parent = _.get(defaultConfig, field);
        return typeof parent === 'boolean' ? parent : fallback;
      };
      const boolOrInherit = (field: string, value: boolean, fallback = false) => (
        !!value === inheritedBool(field, fallback) ? undefined : !!value
      );

      let payload : SettingsType = {
        // requirePlayers/requireCoaches deliberately keep the historical `false`
        // fallback, NOT CONFIG_DEFAULTS.* (which is true for both) — passing the
        // table value would change what gets persisted (an explicit `true` would
        // become "omit/inherit"), and ADR 0035 relocations must not alter write
        // semantics.
        requirePlayers: boolOrInherit('requirePlayers', values.requirePlayers),
        requireCoaches: boolOrInherit('requireCoaches', values.requireCoaches),
        hideTeams: boolOrInherit('hideTeams', values.hideTeams, CONFIG_DEFAULTS.hideTeams),
        hideSchedules: boolOrInherit('hideSchedules', values.hideSchedules, CONFIG_DEFAULTS.hideSchedules),
        hideStandings: boolOrInherit('hideStandings', values.hideStandings, CONFIG_DEFAULTS.hideStandings),
        showAllTeams: boolOrInherit('showAllTeams', values.showAllTeams, CONFIG_DEFAULTS.showAllTeams),
      };
      if (values.requirePlayers) {
        payload.minPlayers = parseInt(values.minPlayers);
        payload.maxPlayers = parseInt(values.maxPlayers);
      }
      if (values.requireCoaches) {
        payload.minCoaches = parseInt(values.minCoaches);
        payload.maxCoaches = parseInt(values.maxCoaches);
      }
      payload.disableSync = boolOrInherit('disableSync', values.disableSync, CONFIG_DEFAULTS.disableSync);

      // Capacity fields, same contract. The form SEEDS capacity from the parent
      // when the entity has none of its own, so writing it back unconditionally
      // stamped the inherited number onto the child. `0` has always meant
      // "inherit" here (resolveEffectiveConfig treats an own capacity of 0 as
      // unset), so both an off toggle and a value identical to the parent's are
      // persisted as "no value of my own".
      const inheritedCapacity = Number(_.get(defaultConfig, 'capacity')) || 0;
      const nextCapacity = values.enableCapacity ? (parseInt(values.capacity) || 0) : 0;
      payload.capacity = (nextCapacity > 0 && nextCapacity !== inheritedCapacity)
        ? nextCapacity
        : undefined;
      // Waitlist settings belong to whichever level owns the capacity.
      payload.hasWaitlist = payload.capacity === undefined ? undefined : !!values.hasWaitlist;
      if (values.enableCapacity && values.hasWaitlist) {
        payload.autoPromote = !!values.autoPromote;
        if (values.autoPromote && values.promotionPaymentWindowHours !== undefined) {
          payload.promotionPaymentWindowHours = parseInt(values.promotionPaymentWindowHours);
        }
      } else {
        // Same reasoning as capacity above — omit rather than stamp `false`.
        payload.autoPromote = undefined;
      }

      // Player eligibility — grouped under `config.eligibility`. Per-sub-key
      // values cascade via getConfig (division > league > tournament >
      // registration). Empty sub-objects are dropped so the saved config
      // clears cleanly.
      if (showConstraints) {
        const eligibility: { gender?: string[]; rating?: any; age?: any } = {};

        const gender = [
          values.genderMale && 'male',
          values.genderFemale && 'female',
          values.genderOther && 'other',
        ].filter(Boolean);
        eligibility.gender = gender;

        const rating: { minRating?: number; maxRating?: number } = {};
        const minR = parseFloat(values.minRating || '');
        const maxR = parseFloat(values.maxRating || '');
        if (!isNaN(minR)) rating.minRating = minR;
        if (!isNaN(maxR)) rating.maxRating = maxR;
        eligibility.rating = rating;

        const age: { minAge?: number; maxAge?: number } = {};
        const minA = parseInt(values.minAge || '', 10);
        const maxA = parseInt(values.maxAge || '', 10);
        if (!isNaN(minA)) age.minAge = minA;
        if (!isNaN(maxA)) age.maxAge = maxA;
        eligibility.age = age;

        (payload as any).eligibility = eligibility;
      }

      // Stats visibility + enabled columns (config.showStats + config.gameStats[sport]).
      //
      // These three are seeded from the PARENT when the entity has no value of
      // its own, so writing them unconditionally stamped an explicit copy of the
      // inherited value onto every entity whose Settings were ever saved — for
      // any unrelated reason. Since `getConfig` is most-specific-wins, that
      // silently froze the level and a later parent-level change (e.g. turning
      // Show Stats off on a contest) could never reach it again.
      //
      // So: only persist a value that actually DIFFERS from what would be
      // inherited. `undefined` is dropped by JSON.stringify, and the server
      // $sets the whole `config` subdoc, so an omitted key is REMOVED — which is
      // exactly "inherit". Never write `false`/`'average'` just because that's
      // what the parent already resolves to.
      const inheritedShowStats = (() => {
        const parent = _.get(defaultConfig, 'showStats');
        if (typeof parent === 'boolean') return parent;
        return CONFIG_DEFAULTS.showStats;
      })();
      (payload as any).showStats = !!values.showStats === inheritedShowStats
        ? undefined
        : !!values.showStats;

      const inheritedStatMode = _.get(defaultConfig, 'statDisplayMode') || CONFIG_DEFAULTS.statDisplayMode;
      const statMode = values.statDisplayMode || CONFIG_DEFAULTS.statDisplayMode;
      (payload as any).statDisplayMode = statMode === inheritedStatMode ? undefined : statMode;

      // gameStats is a sport-keyed map, so drop just THIS sport's entry when it
      // matches the inherited columns — other sports' entries must survive.
      const inheritedGameStats = _.get(defaultConfig, `gameStats.${sport}`)
        || defaultGameStats(sport)
        || [];
      const ownGameStats = values.gameStats || [];
      const nextGameStats = { ..._.get(data, 'config.gameStats', {}) };
      if (_.isEqual([...ownGameStats].sort(), [...inheritedGameStats].sort())) {
        delete nextGameStats[sport];
      } else {
        nextGameStats[sport] = ownGameStats;
      }
      (payload as any).gameStats = _.isEmpty(nextGameStats) ? undefined : nextGameStats;

      // startDate is a TOP-LEVEL entity field — it must never ride inside the
      // config payload (config flows through the no-stamping inherit logic and
      // the server $sets the whole subdoc). Empty input → key omitted → the
      // entity keeps its existing startDate (never cleared from here).
      const topLevel: TopLevelSettings = {};
      if (showStartDate && values.startDate) {
        topLevel.startDate = new Date(values.startDate).toISOString();
      }

      onSubmitSuccess(payload, topLevel)
    } catch (error) {
      console.error('Failed to submit form:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex flex-col flex-1 min-h-0">
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4">
          <Accordion type="multiple" defaultValue={["general", "roster", "visibility"]} className="w-full">
            {/* ── General (top-level entity fields; tournament/league only) ── */}
            {showStartDate && (
              <AccordionItem value="general">
                <AccordionTrigger className="text-base font-semibold">General</AccordionTrigger>
                <AccordionContent className="space-y-4 pt-1">
                  <FormField
                    control={form.control}
                    name="startDate"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Start Date</FormLabel>
                        <FormControl>
                          <Input type="date" {...field} value={field.value ?? ''} />
                        </FormControl>
                        <FormDescription>
                          When this {entityType} begins. Used for ordering and public display.
                        </FormDescription>
                      </FormItem>
                    )}
                  />
                </AccordionContent>
              </AccordionItem>
            )}
            {/* ── Roster ── */}
            <AccordionItem value="roster">
              <AccordionTrigger className="text-base font-semibold">Roster</AccordionTrigger>
              <AccordionContent className="space-y-4 pt-1">
        <FormField
          control={form.control}
          name="requirePlayers"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Require Players</FormLabel>
                <FormDescription>
                  Set minimum and maximum number of players for the team.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        {requirePlayers && (
          <>
            <FormField
              control={form.control}
              name="minPlayers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Players</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      {...field}
                      onChange={e => {
                        const value = parseNumberInput(e.target.value);
                        field.onChange(value !== undefined ? value.toString() : '');
                        form.trigger();
                      }}
                    />
                  </FormControl>
                  <FormDescription>The minimum number of players required for the team.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maxPlayers"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Players</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      {...field}
                      onChange={e => {
                        const value = parseNumberInput(e.target.value);
                        field.onChange(value !== undefined ? value.toString() : '');
                        form.trigger();
                      }}
                    />
                  </FormControl>
                  <FormDescription>The maximum number of players allowed in the team.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
        <FormField
          control={form.control}
          name="requireCoaches"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Require Coaches</FormLabel>
                <FormDescription>
                  Set minimum and maximum number of coaches for the team.
                </FormDescription>
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        {requireCoaches && (
          <>
            <FormField
              control={form.control}
              name="minCoaches"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Minimum Coaches</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      {...field}
                      onChange={e => {
                        const value = parseNumberInput(e.target.value);
                        field.onChange(value !== undefined ? value.toString() : '');
                        form.trigger();
                      }}
                    />
                  </FormControl>
                  <FormDescription>The minimum number of coaches required for the team.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="maxCoaches"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximum Coaches</FormLabel>
                  <FormControl>
                    <Input
                      type="text"
                      {...field}
                      onChange={e => {
                        const value = parseNumberInput(e.target.value);
                        field.onChange(value !== undefined ? value.toString() : '');
                        form.trigger();
                      }}
                    />
                  </FormControl>
                  <FormDescription>The maximum number of coaches allowed for the team.</FormDescription>
                  <FormMessage />
                </FormItem>
              )}
            />
          </>
        )}
              </AccordionContent>
            </AccordionItem>

            {/* ── Visibility ── */}
            <AccordionItem value="visibility">
              <AccordionTrigger className="text-base font-semibold">Visibility</AccordionTrigger>
              <AccordionContent className="space-y-4 pt-1">
        <FormField
          control={form.control}
          name="hideTeams"
          render={({ field }) => (
          <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
            <div className="space-y-0.5">
              <FormLabel className="text-base">Hide Teams</FormLabel>
              <FormDescription>
                Hide teams from the public.
              </FormDescription>
              <InheritedNote field="hideTeams" />
            </div>
            <FormControl>
              <Switch
                checked={field.value}
                onCheckedChange={field.onChange}
              />
            </FormControl>
          </FormItem>
        )} />
        <FormField
          control={form.control}
          name="hideSchedules"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Hide Schedules</FormLabel>
                <FormDescription>
                  Hide schedules from the public.
                </FormDescription>
                <InheritedNote field="hideSchedules" />
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="hideStandings"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Hide Standings</FormLabel>
                <FormDescription>
                  Hide division standings from the public.
                </FormDescription>
                <InheritedNote field="hideStandings" />
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="showAllTeams"
          render={({ field }) => (
            <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
              <div className="space-y-0.5">
                <FormLabel className="text-base">Show All Teams</FormLabel>
                {/* Structured copy (FormDescription is a <p>, which can't hold
                    these paragraphs) — same muted styling. */}
                <div className="space-y-1 text-sm text-muted-foreground">
                  <p>
                    <span className="font-medium text-foreground/80">Off (default):</span> the public
                    division page lists only Active teams, so switching a team&apos;s Active toggle off
                    hides it from visitors.
                  </p>
                  <p>
                    <span className="font-medium text-foreground/80">On:</span> every team in the
                    division shows publicly, including deactivated ones. Useful when teams dropped
                    mid-season or merged but you want the full historical roster visible.
                  </p>
                  <p>
                    Unpaid (pending) and withdrawn teams stay hidden from the public either way.
                  </p>
                </div>
                <InheritedNote field="showAllTeams" />
              </div>
              <FormControl>
                <Switch
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              </FormControl>
            </FormItem>
          )}
        />
              </AccordionContent>
            </AccordionItem>

            {/* ── Stats ── */}
            {(STAT_FIELDS_BY_SPORT[sport]?.length > 0) && (
              <AccordionItem value="stats">
                <AccordionTrigger className="text-base font-semibold">Stats</AccordionTrigger>
                <AccordionContent className="space-y-4 pt-1">
          <div className="space-y-4 rounded-lg border p-4">
            <FormField
              control={form.control}
              name="showStats"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between">
                  <div className="space-y-0.5">
                    <FormLabel className="text-base">Show Stats</FormLabel>
                    <FormDescription>Show player &amp; team stats to the public.</FormDescription>
                  </div>
                  <FormControl>
                    <Switch checked={field.value} onCheckedChange={field.onChange} />
                  </FormControl>
                </FormItem>
              )}
            />
            {form.watch('showStats') && (<>
              <FormField
                control={form.control}
                name="gameStats"
                render={({ field }) => {
                  const selected = field.value || [];
                  return (
                    <FormItem>
                      <FormLabel>Stats to Track</FormLabel>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        {(STAT_FIELDS_BY_SPORT[sport] || []).map((key) => (
                          <label key={key} className="flex items-center space-x-2 cursor-pointer">
                            <Checkbox
                              checked={selected.includes(key)}
                              onCheckedChange={(checked) => {
                                const next = checked
                                  ? [...selected, key]
                                  : selected.filter((k) => k !== key);
                                field.onChange(next);
                              }}
                            />
                            <span className="text-sm font-normal">{(STAT_METADATA_BY_SPORT[sport] || basketballStatMetadata)[key]?.headerName || key}</span>
                          </label>
                        ))}
                      </div>
                      <FormDescription>
                        The stats you&apos;ll record for each game, and what fans see on box scores, standings, and player &amp; team stat pages.
                      </FormDescription>
                    </FormItem>
                  );
                }}
              />
              <FormField
                control={form.control}
                name="statDisplayMode"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Stat Display</FormLabel>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                      {[
                        { value: 'average', label: 'Average per game' },
                        { value: 'total', label: 'Cumulative total' },
                      ].map((opt) => (
                        <label key={opt.value} className="flex items-center space-x-2 cursor-pointer">
                          <input
                            type="radio"
                            name="statDisplayMode"
                            value={opt.value}
                            checked={(field.value || 'average') === opt.value}
                            onChange={() => field.onChange(opt.value)}
                          />
                          <span className="text-sm font-normal">{opt.label}</span>
                        </label>
                      ))}
                    </div>
                    <FormDescription>
                      How aggregate numbers show on the public player &amp; team stat pages and leaderboard.
                    </FormDescription>
                  </FormItem>
                )}
              />
            </>)}
          </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* ── Capacity ── */}
            <AccordionItem value="capacity">
              <AccordionTrigger className="text-base font-semibold">Registration Capacity</AccordionTrigger>
              <AccordionContent className="space-y-3 pt-1">
          <FormField
            control={form.control}
            name="enableCapacity"
            render={({ field }) => (
              <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mb-3">
                <div className="space-y-0.5">
                  <FormLabel className="text-base">Limit team registrations</FormLabel>
                  <FormDescription>Set a maximum number of teams that can register.</FormDescription>
                </div>
                <FormControl>
                  <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                </FormControl>
              </FormItem>
            )}
          />
          {enableCapacity && (
            <>
              <FormField
                control={form.control}
                name="capacity"
                render={({ field }) => (
                  <FormItem className="mb-3">
                    <FormLabel>Max Teams</FormLabel>
                    <FormControl>
                      {/* Text (not number) input so the scroll wheel can't
                          inadvertently change the value; digits-only via filter. */}
                      <Input
                        type="text"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        {...field}
                        onChange={e => field.onChange(e.target.value.replace(/[^0-9]/g, ''))}
                      />
                    </FormControl>
                    <FormDescription>Maximum number of teams accepted into this division.</FormDescription>
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="hasWaitlist"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4 mb-3">
                    <div className="space-y-0.5">
                      <FormLabel className="text-base">Enable waitlist</FormLabel>
                      <FormDescription>Allow teams to join a waitlist when the division is full.</FormDescription>
                    </div>
                    <FormControl>
                      <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                    </FormControl>
                  </FormItem>
                )}
              />
              {hasWaitlist && (
                <>
                  <FormField
                    control={form.control}
                    name="autoPromote"
                    render={({ field }) => (
                      <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3 mb-3">
                        <div className="space-y-0.5">
                          <FormLabel className="text-base">Auto-Promote Waitlist</FormLabel>
                          <FormDescription>Automatically promote the next waitlisted team when a spot opens.</FormDescription>
                        </div>
                        <FormControl>
                          <Switch checked={!!field.value} onCheckedChange={field.onChange} />
                        </FormControl>
                      </FormItem>
                    )}
                  />
                  {form.watch('autoPromote') && (
                    <FormField
                      control={form.control}
                      name="promotionPaymentWindowHours"
                      render={({ field }) => (
                        <FormItem className="mb-3">
                          <FormLabel>Payment Window (hours)</FormLabel>
                          <FormControl>
                            <Input type="number" min="1" {...field} onChange={e => field.onChange(e.target.value)} />
                          </FormControl>
                          <FormDescription>Hours a promoted team has to pay before being demoted back to waitlist.</FormDescription>
                        </FormItem>
                      )}
                    />
                  )}
                </>
              )}
            </>
          )}
              </AccordionContent>
            </AccordionItem>

            {/* ── Eligibility ── */}
            {showConstraints && (
              <AccordionItem value="eligibility">
                <AccordionTrigger className="text-base font-semibold">Eligibility</AccordionTrigger>
                <AccordionContent className="space-y-4 pt-1">
          <div className="space-y-4 rounded-lg border p-4">
            <div>
              <p className="text-sm text-muted-foreground">
                Optional restrictions on who can register for this division. Leave blank for no restriction.
              </p>
            </div>

            {/* Allowed genders */}
            <div className="space-y-2">
              <FormLabel>Allowed genders</FormLabel>
              <div className="flex flex-wrap items-center gap-4">
                <FormField
                  control={form.control}
                  name="genderMale"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">Male</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="genderFemale"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">Female</FormLabel>
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="genderOther"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center space-x-2 space-y-0">
                      <FormControl>
                        <Checkbox checked={!!field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                      <FormLabel className="text-sm font-normal">Other</FormLabel>
                    </FormItem>
                  )}
                />
              </div>
              <FormDescription>Uncheck all for no gender restriction.</FormDescription>
            </div>

            {/* Rating */}
            <div className="space-y-2">
              <FormLabel>Self-rating range</FormLabel>
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="minRating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Min</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="e.g. 3.0"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxRating"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Max</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          step="0.1"
                          min="0"
                          placeholder="e.g. 4.5"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>

            {/* Age */}
            <div className="space-y-2">
              <FormLabel>Age range</FormLabel>
              <div className="grid grid-cols-2 gap-3">
                <FormField
                  control={form.control}
                  name="minAge"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Min</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="e.g. 18"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={form.control}
                  name="maxAge"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs text-muted-foreground">Max</FormLabel>
                      <FormControl>
                        <Input
                          type="number"
                          min="0"
                          placeholder="e.g. 99"
                          {...field}
                          value={field.value || ''}
                          onChange={(e) => field.onChange(e.target.value)}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
            </div>
          </div>
                </AccordionContent>
              </AccordionItem>
            )}

            {/* ── Sync ── */}
            <AccordionItem value="sync">
              <AccordionTrigger className="text-base font-semibold">Sync</AccordionTrigger>
              <AccordionContent className="space-y-4 pt-1">
                <FormField
                  control={form.control}
                  name="disableSync"
                  render={({ field }) => (
                    <FormItem className="flex flex-row items-center justify-between rounded-lg border p-4">
                      <div className="space-y-0.5">
                        <FormLabel className="text-base">Disable Sync</FormLabel>
                        <FormDescription>
                          Turn off automatic synchronization for these teams.
                        </FormDescription>
                      </div>
                      <FormControl>
                        <Switch checked={field.value} onCheckedChange={field.onChange} />
                      </FormControl>
                    </FormItem>
                  )}
                />
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>

        <div className="border-t p-4">
          <Button
            type="submit"
            className="w-full"
            disabled={isSubmitting || !isFormValid()}
            onClick={() => {
              console.log('isFormValid', isFormValid());
            }}
          >
            {isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
        </div>
      </form>
    </Form>
  )
}

